import { type ResolutionString } from '@/shared/lib/charting_library';
import { TimeframeType } from '@/shared/types/timeframe';

/**
 * Validates if a provided string is a valid TimeframeType
 * @param timeframe - The timeframe to validate
 * @returns True if the timeframe is valid
 */
export const isValidTimeframe = (timeframe: string): boolean =>
    Object.values(TimeframeType).includes(timeframe as TimeframeType);

/**
 * Converts a TimeframeType to minutes
 * @param timeframe - The timeframe to convert
 * @returns The equivalent number of minutes
 */
export const getTimeframeInMinutes = (timeframe: TimeframeType): number => {
    switch (timeframe) {
        case TimeframeType.M1:
            return 1;
        case TimeframeType.M5:
            return 5;
        case TimeframeType.M15:
            return 15;
        case TimeframeType.M30:
            return 30;
        case TimeframeType.H1:
            return 60;
        case TimeframeType.H4:
            return 240;
        case TimeframeType.D1:
            return 60 * 24; // 1440 minutes
        case TimeframeType.W1:
            return 60 * 24 * 7; // 10080 minutes (1 week)
        case TimeframeType.MN1:
            return 60 * 24 * 30; // 43200 minutes (1 month, approximation)
        default:
            return 1; // Default to 1 minute if unknown
    }
};

/**
 * Converts a TimeframeType to a TradingView ResolutionString
 * @param timeframe - The timeframe to convert
 * @returns The equivalent TradingView resolution string
 */
export const convertTimeframeToResolution = (
    timeframe: TimeframeType,
): ResolutionString => {
    switch (timeframe) {
        case TimeframeType.M1:
            return '1' as ResolutionString;
        case TimeframeType.M5:
            return '5' as ResolutionString;
        case TimeframeType.M15:
            return '15' as ResolutionString;
        case TimeframeType.M30:
            return '30' as ResolutionString;
        case TimeframeType.H1:
            return '60' as ResolutionString;
        case TimeframeType.H4:
            return '240' as ResolutionString;
        case TimeframeType.D1:
            return '1D' as ResolutionString;
        case TimeframeType.W1:
            return '1W' as ResolutionString;
        case TimeframeType.MN1:
            return '1M' as ResolutionString;
        default:
            return '1' as ResolutionString; // Default to 1 minute if unknown
    }
};

/**
 * Converts a string to a TimeframeType
 * @param value - The input string to convert
 * @returns The corresponding TimeframeType
 * @throws Error if the input string is not a valid TimeframeType
 */
export const stringToTimeframeType = (value: string): TimeframeType => {
    if (!isValidTimeframe(value)) {
        throw new Error(`Invalid timeframe: ${value}`);
    }

    return value as TimeframeType;
};

/**
 * Sorts timeframes in chronological order (M1, M5, M15, M30, H1, H4, D1, etc.)
 * @param timeframes - Array of timeframe strings to sort
 * @returns Sorted array of timeframes in chronological order
 */
export const sortTimeframesChronologically = (timeframes: string[]): string[] =>
    timeframes.sort((a, b) => {
        // Handle special cases first
        if (!isValidTimeframe(a) && !isValidTimeframe(b)) return 0;

        if (!isValidTimeframe(a)) return 1;

        if (!isValidTimeframe(b)) return -1;

        const minutesA = getTimeframeInMinutes(a as TimeframeType);
        const minutesB = getTimeframeInMinutes(b as TimeframeType);

        return minutesA - minutesB;
    });
